import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@fontsource/inter/200.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/800.css";
import Welcome from "./components/views/Welcome";
import Login from "./components/views/Login";
import LoginProgress from "./components/views/LoginProgress";
import LoginError from "./components/views/LoginError";
import MfaMethod from "./components/views/MfaMethod";
import MfaCode from "./components/views/MfaCode";
import MfaProgress from "./components/views/MfaProgress";
import AuthzComplete from "./components/views/AuthzComplete";
import AuthzProgress from "./components/views/AuthzProgress";
import AuthzError from "./components/views/AuthzError";
import AuthzAccounts from "./components/views/AuthzAccounts";
import React from "react";
import ModalContainer from "./components/modals/ModalContainer";
import ErrorBoundary from "./components/components/ErrorBoundary";
import type { SessionStatus } from "./store/appStore";
import { useAppStore } from "./store/appStore";
import { endSession, getSessionStatus } from "./api/api";
import GenericError from "./components/views/GenericError";
import MfaCodeUserLockedError from "./components/views/MfaCodeUserLockedError";
import MfaCodeMaxAttemptsError from "./components/views/MfaCodeMaxAttemptsError";

const App = () => {
	const { sessionStatus, setSessionStatus, forceCheckSessionStatus, setForceCheckSessionStatus, utilityResponse, setUtilityResponse, setMaskedPhoneNumber, setMaskedEmailAddress, resetStore } = useAppStore();
	console.info({ sessionStatus });
	const [appHasInitialized, setAppHasInitialized] = useState(false);

	// Determine where we should postMessages if this is being embedded in an iframe
	const isEmbedded = window.parent && window.parent !== window;
	let onRegOrigin = "https://0.0.0.0:3002/";
	if (isEmbedded) {
		if (window.location.hostname === "dev.authz.kingenergy.com") {
            onRegOrigin = "https://dev.registration.kingenergy.com";
        } else if (window.location.hostname === "authz.kingenergy.com") {
            onRegOrigin = "https://registration.kingenergy.com";
        }
	}
	
	useEffect(() => {
		setAppHasInitialized(true);
	}, []);

	useEffect(() => {
		// Only start polling after the first pass has completed …
		if (!appHasInitialized) return;

		// Don't poll if we're in a state where the session status is not going to change
		const terminalStates: SessionStatus[] = [
			"INITIALIZED",
			"LOGIN_WAITING",
			"LOGIN_ERROR",
			"MFA_METHOD_WAITING",
			"MFA_METHOD_ERROR",
			"MFA_CODE_WAITING_SMS",
			"MFA_CODE_WAITING_EMAIL",
			"MFA_CODE_ERROR_SMS",
			"MFA_CODE_ERROR_EMAIL",
			"MFA_CODE_ERROR_USER_LOCKED",
			"AUTHZ_WAITING",
			"AUTHZ_SUCCESS",
			"CANCELLED",
			"AUTHZ_ERROR",
		];
		// forceCheckSessionStatus acts as a flag to override the terminal state check since 
		// the sessionState returned from API endpoints like submitCredentials is not always deterministic
		// so we want to force checking the session status one more time after the session is in a terminal state.
		if (terminalStates.includes(sessionStatus as SessionStatus) && !forceCheckSessionStatus) return;

		const POLLING_INTERVAL_MS = 1000;
		const id = setInterval(() => {
			setForceCheckSessionStatus(false);
			checkAndSetSessionStatus();
		}, POLLING_INTERVAL_MS);

		return () => clearInterval(id); // cleanup on unmount
	}, [appHasInitialized, sessionStatus, forceCheckSessionStatus]);

	const checkAndSetSessionStatus = async () => {
        const sessionId = getSessionId();
        if (sessionId == null) {
            // If no session ID, reset to initial state and stop polling
            resetStore();
            return;
        }
        const response = await getSessionStatus(sessionId);
        const newStatus = response.status.status as SessionStatus;
        if (newStatus !== sessionStatus) {
            console.info({ response, newStatus });
            setSessionStatus(newStatus);
        }
		if (response.status.utilityResponse != null) {
			setUtilityResponse(response.status.utilityResponse);
			if (response.status.utilityResponse.phoneNumber != null) {
				setMaskedPhoneNumber(response.status.utilityResponse.phoneNumber);
			}
			if (response.status.utilityResponse.email != null) {
				setMaskedEmailAddress(response.status.utilityResponse.email);
			}
		}
    };

	const getRouteFromSessionStatus = (sessionStatus: SessionStatus): string => {
		if (sessionStatus == null) {
			return "/";
		}

		switch (sessionStatus) {
            case "CANCELLED":
                return "/";

            case "ACCOUNT_LIST_ERROR":
            case "MFA_METHOD_ERROR":
                return "/error";

            case "LOGIN_WAITING":
                return "/login";

            case "LOGIN_SUBMITTING":
                return "/login/progress";

            case "LOGIN_ERROR":
                return "/login/error";

            case "LOGIN_SUCCESS":
                return "/login/progress";

            case "MFA_METHOD_WAITING":
                return "/mfa/method";

            case "MFA_CODE_WAITING_SMS":
                return "/mfa/code/sms";

            case "MFA_CODE_WAITING_EMAIL":
                return "/mfa/code/email";

            case "MFA_METHOD_SUBMITTING":
            case "MFA_CODE_SUBMITTING":
            case "ACCOUNT_LIST_SUBMITTING":
                return "/mfa/progress";

            case "MFA_CODE_ERROR_SMS":
                return "/mfa/error/code/sms";

            case "MFA_CODE_ERROR_EMAIL":
                return "/mfa/error/code/email";
			
			case "MFA_CODE_ERROR_USER_LOCKED":
				return "/mfa/error/user-locked";
			
			case "MFA_CODE_ERROR_MAX_ATTEMPTS":
				return "/mfa/error/max-attempts";

            case "AUTHZ_WAITING":
            case "ACCOUNT_LIST_SUCCESS":
                return "/authz/accounts";

            case "AUTHZ_SUBMITTING":
                return "/authz/progress";

            case "AUTHZ_ERROR":
                return "/authz/error";

            case "AUTHZ_SUCCESS":
                return "/authz/complete";

            default:
                return "/";
        }
	}

	const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
		const location = useLocation();
		
		// If not initialized yet, show loading or nothing
		if (!appHasInitialized) {
			return null;
		}

		// Get the target path based on session status
		const targetPath = getRouteFromSessionStatus(sessionStatus);
		console.info('ProtectedRoute:', { 
			sessionStatus, 
			currentPath: location.pathname,
			targetPath
		});

		// If we're not on the target path, redirect
		if (targetPath !== location.pathname) {
			console.info('Redirecting to:', targetPath);
			return <Navigate to={targetPath} replace />;
		}
		
		return <>{children}</>;
	};

	const getSessionId = (): string | null => {
			let session: { sessionId: string, createdAt: Date, expiresAt: Date } | null = null;
			const sessionString = sessionStorage.getItem('session');
			if (sessionString != null && sessionString !== '') {
				session = JSON.parse(sessionString);
				if (session?.sessionId != null) {
					return session.sessionId;
				}
			}

			console.info({ getSessionId: 'no session found' });
			return null;
	}

	function cancelSession(): void {
        const sessionId = getSessionId();
        if (sessionId != null) {
            endSession(sessionId);
        }
		
		// Clear all cached data
		sessionStorage.clear();
		localStorage.clear();

		resetStore();
    }

	return (
		<BrowserRouter>
			<ErrorBoundary>
				<Routes>
					<Route path="/" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
					<Route path="/error" element={<ProtectedRoute><GenericError getSessionId={getSessionId} cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/login" element={<ProtectedRoute><Login /></ProtectedRoute>} />
					<Route path="/login/progress" element={<ProtectedRoute><LoginProgress cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/login/error" element={<ProtectedRoute><LoginError cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/mfa/method" element={<ProtectedRoute><MfaMethod getSessionId={getSessionId} cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/mfa/code/sms" element={<ProtectedRoute><MfaCode codeType="SMS" getSessionId={getSessionId} cancelSession={cancelSession} error={false} utilityResponse={utilityResponse} /></ProtectedRoute>} />
					<Route path="/mfa/code/email" element={<ProtectedRoute><MfaCode codeType="EMAIL" getSessionId={getSessionId} cancelSession={cancelSession} error={false} utilityResponse={utilityResponse} /></ProtectedRoute>} />
					<Route path="/mfa/progress" element={<ProtectedRoute><MfaProgress cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/mfa/error/code/sms" element={<ProtectedRoute><MfaCode cancelSession={cancelSession} getSessionId={getSessionId} codeType="SMS" error={true} utilityResponse={utilityResponse} /></ProtectedRoute>} />
					<Route path="/mfa/error/code/email" element={<ProtectedRoute><MfaCode cancelSession={cancelSession} getSessionId={getSessionId} codeType="EMAIL" error={true} utilityResponse={utilityResponse} /></ProtectedRoute>} />
					<Route path="/mfa/error/user-locked" element={<ProtectedRoute><MfaCodeUserLockedError cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/mfa/error/max-attempts" element={<ProtectedRoute><MfaCodeMaxAttemptsError cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/authz/accounts" element={<ProtectedRoute><AuthzAccounts getSessionId={getSessionId} accounts={utilityResponse} cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/authz/progress" element={<ProtectedRoute><AuthzProgress cancelSession={cancelSession} /></ProtectedRoute>} />
					<Route path="/authz/complete" element={<ProtectedRoute><AuthzComplete utilityResponse={utilityResponse} getSessionId={getSessionId} isEmbedded={isEmbedded} onRegOrigin={onRegOrigin} /></ProtectedRoute>} />
					<Route path="/authz/error" element={<ProtectedRoute><AuthzError utilityResponse={utilityResponse} cancelSession={cancelSession} getSessionId={getSessionId} /></ProtectedRoute>} />
				</Routes>
				<ErrorBoundary>
					<ModalContainer />
				</ErrorBoundary>
			</ErrorBoundary>
		</BrowserRouter>
	);
};

export default App;
